'use client'

import {
  type ILoadNotification,
  SubscribeNotificationProvider,
  useNotifications as useNotificationsContext,
} from '@repo/sdk'
import { toast, ToastNotification, useCustomToast } from '@repo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import React from 'react'

import { parseNotificationData } from './parse-notification-data.tsx'

type SubscribeFn = Parameters<typeof SubscribeNotificationProvider>['0']['subscribeFn']
type SubscribeFnParams = Parameters<SubscribeFn>[0]

export const useSubscribeNotification = (): { subscribeFn: SubscribeFn } => {
  const loadChannel = useLoadsChannel()
  const loadDocumentsChannel = useLoadDocumentsChannel()

  return {
    subscribeFn: props => {
      loadChannel.subscribe(props)
      loadDocumentsChannel.subscribe(props)

      return () => {
        loadChannel.unsubscribe(props)
        loadDocumentsChannel.unsubscribe(props)
      }
    },
  }
}

type ChannelHook = () => {
  subscribe: (props: SubscribeFnParams) => void
  unsubscribe: (props: SubscribeFnParams) => void
}

const useLoadsChannel: ChannelHook = () => {
  const { newToast } = useCustomToast()
  const router = useRouter()
  const { addNotification } = useNotificationsContext()
  const queryClient = useQueryClient()

  return {
    subscribe: ({ pusher, userId }) => {
      const loadsChannel = pusher.subscribe(`private-user-${userId}-loads`)

      loadsChannel.bind('bidding.newBid', (notification: ILoadNotification) => {
        queryClient.refetchQueries({
          queryKey: ['/inquiry-loads/user'],
        })

        toast.message(`${notification.quote_id} quoted`, {
          action: {
            label: 'Check out',
            onClick: () => {
              router.push(`/quotes/${notification.quote_id}`)
            },
          },
          description: 'Check out your quote',
          position: 'top-right',
        })
      })

      loadsChannel.bind('bidding.finished', (notification: ILoadNotification) => {
        const parseNotification = parseNotificationData({
          notification: {
            load: notification,
            type: 'bidding.finished',
          },
        })

        addNotification(parseNotification)

        return newToast(<ToastNotification isToast notification={parseNotification} />)
      })
    },
    unsubscribe: ({ pusher, userId }) => {
      pusher.unsubscribe(`private-user-${userId}-loads`)
    },
  }
}

const useLoadDocumentsChannel: ChannelHook = () => {
  const { newToast } = useCustomToast()
  const { addNotification } = useNotificationsContext()

  return {
    subscribe: ({ pusher, userId }) => {
      const loadChannel = pusher.subscribe(`private-loads-documents-user-${userId}`)

      loadChannel.bind('document.created', (notification: ILoadNotification) => {
        const parseNotification = parseNotificationData({
          notification: {
            load: notification,
            type: 'inquiry-load-new-admin-bid',
          },
        })

        addNotification(parseNotification)

        return newToast(<ToastNotification isToast notification={parseNotification} />)
      })
    },
    unsubscribe: ({ pusher, userId }) => {
      pusher.unsubscribe(`private-loads-documents-user-${userId}`)
    },
  }
}
